import React, { FC } from 'react';

import { Td, Tr } from '@chakra-ui/react';
import noop from 'lodash/noop';
import { INIT_PAGE_INFO } from 'services/utils/constants';

import { TableBody } from 'components/DataDisplay/NewTable/TableBody';
import { TableColumnProps } from 'components/DataDisplay/NewTable/types';

import { NestedPagerRow } from './NestedPagerRow';
import { NestedStatusRow } from './NestedStatusRow';
import { TableDetails } from './TableDetails';
import { ROW_CELL_STYLES } from './useDataAccessColumns';
import {
  DataAccessIdentity,
  useDataAccessTables,
} from './useDataAccessQueries';
import { DataAccessViewActions } from './useDataAccessView';
import {
  DataAccessRow,
  DataAccessViewState,
  getErrorMessage,
  toTableRow,
} from './utils';

interface TableRowsProps {
  identity: DataAccessIdentity;
  schema: DataAccessRow;
  view: DataAccessViewState;
  actions: DataAccessViewActions;
  columns: TableColumnProps<DataAccessRow>[];
}

/**
 * One page of an open schema's tables, read live, with its own pager. A
 * click on a table opens its details under it.
 */
export const TableRows: FC<TableRowsProps> = props => {
  const { identity, schema, view, actions, columns } = props;
  const page = view.tablePages[schema.uuid] ?? INIT_PAGE_INFO;
  const query = useDataAccessTables(identity, schema.uuid, view.filters, page);
  const { data } = query;

  if (query.isError && !data)
    return (
      <NestedStatusRow
        depth={2}
        error={getErrorMessage(query.error, 'Failed to load tables')}
        onRetry={() => query.refetch()}
      />
    );
  if (!data) return <NestedStatusRow depth={2} isLoading />;

  const sensitivityAvailable = data.sensitivity_available ?? true;
  const rows = (data.items ?? []).map(table =>
    toTableRow(table, view.openTable === table.uuid, sensitivityAvailable),
  );
  const total = data.total ?? 0;

  return (
    <>
      {rows.length ? (
        // The table's own row component, so these rows match the database rows.
        rows.map((row, index) => (
          <TableBody
            key={row.uuid}
            columns={columns}
            row={row}
            index={index}
            rowIndex={-1}
            setCurrentRowIndex={noop}
            isRowActive={undefined}
            cursor="pointer"
            onRowClick={table => actions.toggleTable(table.uuid)}
            styles={{
              cell: ROW_CELL_STYLES,
              row: { opacity: query.isPlaceholderData ? 0.5 : 1 },
            }}
            expanded
            subComponent={({ row: table }) =>
              table.isOpen ? (
                <Tr bg="white">
                  <Td colSpan={100} p={0}>
                    <TableDetails identity={identity} table={table} />
                  </Td>
                </Tr>
              ) : null
            }
          />
        ))
      ) : (
        <NestedStatusRow depth={2} message="No tables match these filters" />
      )}
      {/* A failed refresh keeps the rows it had, with the error under them. */}
      {query.isError && (
        <NestedStatusRow
          depth={2}
          error={getErrorMessage(query.error, 'Failed to load tables')}
          onRetry={() => query.refetch()}
        />
      )}
      {(total > page.page_size || page.page_number > 1) && (
        <NestedPagerRow
          total={total}
          pageInfo={page}
          label={`tables in ${schema.name}`}
          isFetching={query.isFetching}
          onChange={info => actions.setTablePage(schema.uuid, info)}
        />
      )}
    </>
  );
};
